<template>
  <slice
    :id="block._key"
    key="slice-gallery"
    class="slice--gallery"
    :class="{
      container: block.with_container,
      'with-gap': block.with_gap,
    }"
  >
    <div
      v-for="(item, index) in matrix"
      :key="item._key"
      v-waypoint="{
        active: !imagesSeen.includes(index),
        callback: (w) => onImageWaypoint(w, index),
        options: intersectionOptions,
      }"
      class="slice--gallery__item"
      :style="{
        '--grid-column-mobile': item.columnMobile,
        '--grid-row-mobile': item.rowMobile,
        '--grid-column': item.column,
        '--grid-row': item.row,
      }"
    >
      <div
        v-if="item.render === 'Video'"
        class="slice--gallery__video"
        :class="`${item.videoFolder.toLowerCase().replace(/ /g, '-')}`"
        style="position: relative"
        :style="`--padding-top: ${item.ratio}%; --padding-top-mobile: ${item.ratioMobile}%;`"
      >
        <video-bunny
          :src="`https://iframe.mediadelivery.net/embed/${
            BUNNY_STREAM_FOLDERS[item.videoFolder]
          }/${
            item.videoId
          }?responsive=true&autoplay=true&loop=true&muted=true&preload=true`"
        />
      </div>
      <div
        v-else-if="item.render === 'Edito'"
        class="slice--gallery__edito"
        :class="`slice--gallery__edito--${item.editoColor}`"
      >
        <portable-text :blocks="item.edito" />
      </div>
      <image-with-products
        v-else
        :image-desktop="{
          image: item.image,
          widths: item.widths,
          sizes: item.sizes,
        }"
        :image-mobile="{
          image: item.imageMobile,
          widths: item.widthsMobile,
          sizes: item.sizesMobile,
        }"
      />
    </div>
  </slice>
</template>

<script>
import Slice from '@/components/Slices/Slice'
import PortableText from '@/components/RichText/PortableText'
import VideoBunny from '@/components/video/bunny'
import ImageWithProducts from '@/components/Image/ImageWithProducts'

import { BUNNY_STREAM_FOLDERS } from '@/const'

import { getSanityBunnyImageLink } from '@/utils/image'
import { GALLERY_IMAGE_FORMAT } from '~/const/image'

const ratios = {
  portrait: (640 / 480) * 100,
  landscape: (640 / 960) * 100,
  landscapeFull: (1080 / 1920) * 100,
  square: (960 / 720) * 100,
}

export default {
  name: 'BlockGallery',
  components: {
    Slice,
    PortableText,
    VideoBunny,
    ImageWithProducts,
  },

  props: {
    block: { type: Object, required: true },
  },

  data: () => ({
    intersectionLoadingOptions: {
      root: null,
      rootMargin: '250px 0px 250px 0px',
      threshold: [0, 1],
    },
    intersectionOptions: {
      root: null,
      rootMargin: '0px 0px 0px 0px',
      threshold: [0, 1],
    },
    gallery: null,
    matrix: [],
    imagesSeen: [],
    gridRows: '',
    BUNNY_STREAM_FOLDERS,
  }),

  mounted() {
    this.load()
  },

  methods: {
    onWaypoint(waypoint) {
      if (waypoint.going === this.$waypointMap.GOING_IN) {
        this.load()
      }
    },
    onImageWaypoint(waypoint, index) {
      if (waypoint.going === this.$waypointMap.GOING_IN) {
        waypoint.el.classList.add('is-visible')
        this.imagesSeen.push(index)
      }
    },
    load() {
      if (this.init) return

      this.initMatrix()
    },
    initMatrix() {
      if (this.block.gallery || this.block.gallery_mobile) {
        const matrix = []
        let currentLine = 1
        let currentColumn = 0

        let matrixIndex = 0

        let currentLineMobile = 1
        let currentColumnMobile = 0

        let matrixIndexMobile = 0

        for (const item of this.block.children) {
          const img = {
            render: 'Image',
            image: null,
            imageMobile: null,
            sizes: null,
            widths: null,
            sizesMobile: null,
            widthsMobile: null,
            products: [],
            productsPosition: null,
            videoId: null,
            videoFolder: null,
            edito: null,
            editoColor: null,
            type: '',
            column: '',
            row: '',
            ratio: ratios.portrait,
            ratioMobile: ratios.portrait,
          }

          if (item._type === 'child_video') {
            img.render = 'Video'
            img.videoId = item.video.id
            img.videoFolder = item.video.folder
          } else if (item._type === 'child_edito') {
            img.render = 'Edito'
            img.edito = item.text
            img.editoColor = item.color
          }

          if (this.block.gallery) {
            const line = this.block.gallery[matrixIndex]

            switch (line) {
              case '1 produit portrait':
              case '1 produit paysage':
                img.type =
                  line === '1 produit paysage' ? 'full-landscape' : 'full'
                img.column = `1 / 13`
                img.row = `${currentLine} / ${currentLine + 1}`
                img.sizes = '100vw'
                img.widths = [1170, 1600, 2400, 3200]
                img.ratio =
                  line === '1 produit paysage'
                    ? ratios.landscapeFull
                    : ratios.landscape

                if (item._type === 'child_image') {
                  if (line === '1 produit paysage') {
                    img.image = {
                      url: getSanityBunnyImageLink(
                        item.image.image,
                        GALLERY_IMAGE_FORMAT.LANDSCAPE
                      ),
                      dimensions: GALLERY_IMAGE_FORMAT.LANDSCAPE,
                    }
                  } else {
                    img.image = {
                      url: getSanityBunnyImageLink(
                        item.image.image,
                        GALLERY_IMAGE_FORMAT.PORTRAIT
                      ),
                      dimensions: GALLERY_IMAGE_FORMAT.PORTRAIT,
                    }
                  }
                }

                currentLine++
                matrixIndex++
                currentColumn = 0
                break
              case '2 produits':
                img.type = 'half'
                img.row = `${currentLine} / ${currentLine + 1}`
                img.sizes = '50vw'
                img.widths = [720, 1440, 2160]
                img.ratio = ratios.square

                if (item._type === 'child_image') {
                  img.image = {
                    url: getSanityBunnyImageLink(
                      item.image.image,
                      GALLERY_IMAGE_FORMAT.HALF
                    ),
                    dimensions: GALLERY_IMAGE_FORMAT.HALF,
                  }
                }

                if (currentColumn === 0) {
                  img.column = '1 / 7'
                  currentColumn++
                } else {
                  img.column = '7 / 13'
                  currentLine++
                  matrixIndex++
                  currentColumn = 0
                }
                break
              case '3 produits':
                img.type = 'list'
                img.row = `${currentLine} / ${currentLine + 1}`
                img.sizes = '33vw'
                img.widths = [480, 960, 1440]
                img.ratio = ratios.portrait

                if (item._type === 'child_image') {
                  img.image = {
                    url: getSanityBunnyImageLink(
                      item.image.image,
                      GALLERY_IMAGE_FORMAT.PORTRAIT
                    ),
                    dimensions: GALLERY_IMAGE_FORMAT.PORTRAIT,
                  }
                }

                if (currentColumn === 0) {
                  img.column = '1 / 5'
                  currentColumn++
                } else if (currentColumn === 1) {
                  img.column = '5 / 9'
                  currentColumn++
                } else {
                  img.column = '9 / 13'
                  currentLine++
                  matrixIndex++
                  currentColumn = 0
                }
                break
              case '1 produit portrait - 1 produit paysage':
                img.row = `${currentLine} / ${currentLine + 1}`

                if (currentColumn === 0) {
                  img.type = 'list'
                  img.column = '1 / 5'
                  img.sizes = '33vw'
                  img.widths = [480, 960, 1440]
                  img.ratio = ratios.portrait

                  if (item._type === 'child_image') {
                    img.image = {
                      url: getSanityBunnyImageLink(
                        item.image.image,
                        GALLERY_IMAGE_FORMAT.PORTRAIT
                      ),
                      dimensions: GALLERY_IMAGE_FORMAT.PORTRAIT,
                    }
                  }

                  currentColumn++
                } else {
                  img.type = 'landscape'
                  img.column = '5 / 13'
                  img.sizes = '66vw'
                  img.widths = [960, 1920, 2880]
                  img.ratio = ratios.landscape

                  if (item._type === 'child_image') {
                    img.image = {
                      url: getSanityBunnyImageLink(
                        item.image.image,
                        GALLERY_IMAGE_FORMAT.TWO_PORTRAIT
                      ),
                      dimensions: GALLERY_IMAGE_FORMAT.TWO_PORTRAIT,
                    }
                  }

                  currentLine++
                  matrixIndex++
                  currentColumn = 0
                }
                break
              case '1 produit paysage - 1 produit portrait':
                img.row = `${currentLine} / ${currentLine + 1}`

                if (currentColumn === 0) {
                  img.type = 'landscape'
                  img.column = '1 / 9'
                  img.sizes = '66vw'
                  img.widths = [960, 1920, 2880]
                  img.ratio = ratios.landscape

                  if (item._type === 'child_image') {
                    img.image = {
                      url: getSanityBunnyImageLink(
                        item.image.image,
                        GALLERY_IMAGE_FORMAT.TWO_PORTRAIT
                      ),
                      dimensions: GALLERY_IMAGE_FORMAT.TWO_PORTRAIT,
                    }
                  }
                  currentColumn++
                } else {
                  img.type = 'list'
                  img.column = '9 / 13'
                  img.sizes = '33vw'
                  img.widths = [480, 960, 1440]
                  img.ratio = ratios.portrait
                  if (item._type === 'child_image') {
                    img.image = {
                      url: getSanityBunnyImageLink(
                        item.image.image,
                        GALLERY_IMAGE_FORMAT.PORTRAIT
                      ),
                      dimensions: GALLERY_IMAGE_FORMAT.PORTRAIT,
                    }
                  }

                  currentLine++
                  matrixIndex++
                  currentColumn = 0
                }
                break
            }

            if (!this.block.gallery[matrixIndex]) {
              matrixIndex = 0
            }
          }

          if (
            this.block.gallery_mobile &&
            this.block.gallery_mobile[matrixIndexMobile]
          ) {
            const lineMobile = this.block.gallery_mobile[matrixIndexMobile]

            switch (lineMobile) {
              case '1 produit portrait':
              case '1 produit paysage':
                img.typeMobile =
                  lineMobile === '1 produit paysage' ? 'full-landscape' : 'full'
                img.columnMobile = `1 / 13`
                img.rowMobile = `${currentLineMobile} / ${
                  currentLineMobile + 1
                }`
                img.sizesMobile = '100vw'
                img.widthsMobile = [390, 780, 1170]

                img.ratioMobile =
                  lineMobile === '1 produit paysage'
                    ? ratios.landscapeFull
                    : ratios.portrait

                if (item._type === 'child_image') {
                  if (lineMobile === '1 produit paysage') {
                    img.imageMobile = {
                      url: getSanityBunnyImageLink(
                        item.image.image_mobile
                          ? item.image.image_mobile
                          : item.image.image,
                        GALLERY_IMAGE_FORMAT.LANDSCAPE
                      ),
                      dimensions: GALLERY_IMAGE_FORMAT.LANDSCAPE,
                    }
                  } else {
                    img.imageMobile = {
                      url: getSanityBunnyImageLink(
                        item.image.image_mobile
                          ? item.image.image_mobile
                          : item.image.image,
                        GALLERY_IMAGE_FORMAT.PORTRAIT
                      ),
                      dimensions: GALLERY_IMAGE_FORMAT.PORTRAIT,
                    }
                  }
                }
                currentLineMobile++
                matrixIndexMobile++
                currentColumnMobile = 0
                break
              case '2 produits':
                img.typeMobile = 'half'
                img.rowMobile = `${currentLineMobile} / ${
                  currentLineMobile + 1
                }`
                img.ratioMobile = ratios.portrait
                img.sizesMobile = '50vw'
                img.widthsMobile = [260, 390, 480]

                if (item._type === 'child_image') {
                  img.imageMobile = {
                    url: getSanityBunnyImageLink(
                      item.image.image_mobile
                        ? item.image.image_mobile
                        : item.image.image,
                      GALLERY_IMAGE_FORMAT.MOBILE_HALF
                    ),
                    dimensions: GALLERY_IMAGE_FORMAT.MOBILE_HALF,
                  }
                }

                if (currentColumnMobile === 0) {
                  img.columnMobile = '1 / 7'
                  currentColumnMobile++
                } else {
                  img.columnMobile = '7 / 13'
                  currentLineMobile++
                  matrixIndexMobile++
                  currentColumnMobile = 0
                }
                break
            }

            if (!this.block.gallery_mobile[matrixIndexMobile]) {
              matrixIndexMobile = 0
            }
          }

          matrix.push(img)
        }

        this.matrix = matrix
      }
    },
  },
}
</script>

<style lang="scss">
.slice--gallery {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-template-rows: auto;
  grid-gap: 0;

  &.container {
    @include mq($from: tablet, $until: large) {
      max-width: calc(100% * (10 / 12));
    }
  }

  &__item {
    position: relative;
    overflow: hidden;
    transform: translateY(10);
    opacity: 0;
    transition: all 0.5s var(--asphalte-animation-function);
    grid-column: var(--grid-column-mobile);
    grid-row: var(--grid-row-mobile);

    @include mq($from: tablet) {
      grid-column: var(--grid-column);
      grid-row: var(--grid-row);
    }

    &.is-visible {
      transform: translateY(0);
      opacity: 1;
    }
  }

  &.with-gap {
    gap: calc(var(--spacing) * 0.2);
  }
}

.slice--gallery__edito {
  background-position: center;
  background-size: cover;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: calc(var(--spacing) * 2);

  @include mq($until: tablet) {
    min-height: calc(100vw * 4 / 3);
  }

  @include mq($from: tablet) {
    padding: calc(var(--spacing) * 4);
  }

  h2 {
    font-size: var(--jumbo-font-size);
  }

  &--secondary {
    --title-color: var(--tertiary-color);

    background-color: var(--secondary-color);
    background-image: url('~/assets/img/background-blue.jpg');

    > * {
      color: var(--tertiary-color);
    }
  }

  &--tertiary {
    --title-color: var(--secondary-color);

    background-color: var(--tertiary-color);
    background-image: url('~/assets/img/background-off-white.jpg');

    > * {
      color: var(--secondary-color);
    }
  }
}

.slice--gallery__video {
  padding-top: var(--padding-top-mobile);

  &.snacks {
    pointer-events: none;
  }

  @include mq($from: tablet) {
    padding-top: var(--padding-top);
  }
}
</style>
